// @flow

import React, { type Node } from 'react';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type Country } from '@tectonics/prs/country';
import { CreditCardForm, DebitCardForm } from './credit-card';
import { Empty } from '@tectonics/look-and-feel/shopify/commons/atoms';
import { PagosnetForm } from './pagosnet';
import { PaynetForm } from './paynet';
import { EftForm } from './eft-form';
import { FieldArgentineCashBankList } from './argentine-cash';
import { SafetypayCashForm } from './safetypay-cash';
import { SafetypayOnlineForm } from './safetypay-online';
import { EWalletForm } from './ewallet-form';
import { ExternalPaymentProviderFormDeciderForColombia } from './achpse-form';

export function PaymentMethod(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');

  if (!selectedPaymentMethod) {
    return null;
  }

  switch (selectedPaymentMethod) {
    case 'creditcard':
      return <CreditCardForm />;

    case 'debitcard':
      return <DebitCardForm />;

    case 'cash':
      return resolveCashPaymentMethodForm(country);

    case 'eft':
      return <EftForm />;

    case 'paynet':
      return <PaynetForm />;

    case 'banktransfer':
    case 'boleto':
    case 'multicaja':
    case 'oxxo':
    case 'oxxopay':
    case 'safetypay':
    case 'sencillito':
    case 'servipag':
    case 'spei':
    case 'webpay':
    case 'pix':
      return <Empty />;

    case 'ewallet':
      return resolveEwalletForm(country);

    case 'safetypay-cash':
      return <SafetypayCashForm />;

    case 'safetypay-online':
      return <SafetypayOnlineForm />;

    case 'externalpayprovider':
      return resolveExternalPayProviderForm(country);

    default:
      throw new Error(`There is no form implementation for ${selectedPaymentMethod.code}`);
  }
}

function resolveCashPaymentMethodForm(country: Country): Node {
  switch (country) {
    case 'ar':
      return <FieldArgentineCashBankList />;
    case 'bo':
      return <PagosnetForm />;
    case 'co':
    case 'mx':
      return <Empty />;

    default:
      throw new Error(`PaymentMethodForm - cash - there is no cash form for country: "${country}".`);
  }
}

function resolveEwalletForm(country: Country): Node {
  switch (country) {
    case 'co':
      return <Empty/>;
    default:
      return <EWalletForm country={country}/>;
  }
}

function resolveExternalPayProviderForm(country: Country): Node {
  switch (country) {
    case 'co':
      return <ExternalPaymentProviderFormDeciderForColombia/>;

    default:
      throw new Error(`PaymentMethodForm - externalpayprovider - there is no externalpayprovider form for country: "${country}".`);
  }
}
